import { defineStore } from 'pinia'
import httpClient from '@/api/httpClient'
import { withIDKeys } from '@/utils/state_helpers.js'
import { useMessagesStore } from '@/stores/messages'
import { useStoryStore } from '@/stores/story'

function newStoryPublication(publicationId) {
  return {
    id: null,
    story_id: null,
    publication_id: publicationId,
    content_type_key: null,
    story_template_id: null,
    exported_at: null,
    subsite_ids: []
  }
}

export const useStoryPublicationsStore = defineStore('storyPublications', {
  state: () => ({
    storyPublications: {}
  }),

  getters: {
    byPublicationId: ({ storyPublications }) => (publicationId) => {
      return Object.values(storyPublications).find(
        storyPub => storyPub.publication_id === publicationId
      ) || newStoryPublication(publicationId)
    }
  },

  actions: {
    async fetch({ storyId }) {
      return httpClient
        .get(`/api/internal/stories/${storyId}/story_publications`)
        .then(({ data: { story_publications } }) => {
          this.storyPublications = withIDKeys(story_publications)
        })
        .catch(err => {
          console.error(err)
          useMessagesStore().addError(err)
        })
    },

    async create({ story_publication }) {
      const { story_id } = story_publication

      return httpClient
        .post(`/api/internal/stories/${story_id}/story_publications/`, { story_publication })
        .then(({ data: { story_publication } }) => {
          this.storyPublications = { ...this.storyPublications, [story_publication.id]: story_publication }
          useMessagesStore().addNotice('Publication attached')
        })
        .catch(err => {
          console.error(err)
          useMessagesStore().addError(err)
          throw err
        })
    },

    async update({ story_publication }) {
      const { story_id } = story_publication

      return httpClient
        .patch(
          `/api/internal/stories/${story_id}/story_publications/${story_publication.id}`,
          { story_publication }
        )
        .then(({ data: { story_publication } }) => {
          this.storyPublications = { ...this.storyPublications, [story_publication.id]: story_publication }
          // Keep client lock_version in sync — StoryPublication touch: true increments it.
          // Only advance, never regress: parallel updates resolve out-of-order and we
          // must not let a stale response overwrite a higher version already applied.
          if (story_publication.story_lock_version != null &&
              story_publication.story_lock_version > useStoryStore().lock_version) {
            useStoryStore().lock_version = story_publication.story_lock_version
          }
          useMessagesStore().addNotice('Publication saved')
        })
        .catch(err => {
          console.error(err)
          useMessagesStore().addError(err)
          throw err
        })
    },

    async updateAll() {
      return Promise.all(
        Object.values(this.storyPublications).map(story_publication =>
          this.update({ story_publication })
        )
      )
    },

    async destroy({ story_publication: { id, story_id } }) {
      return httpClient
        .delete(`/api/internal/stories/${story_id}/story_publications/${id}`)
        .then(() => {
          const { [id]: _removed, ...rest } = this.storyPublications
          this.storyPublications = rest
          useMessagesStore().addNotice('Publication removed')
        })
        .catch(err => {
          console.error(err)
          useMessagesStore().addError(err)
        })
    }
  }
})
